import apiClient from '@/lib/api-client';
import type { AuthTokens, User } from '@/types/auth';
import type {
  AchatPhoto,
  CartePhotographe,
  EtatVente,
  FichePhotographe,
  FormatCatalogue,
  PagePhotographe,
  PhotoPhotographe,
  PhotographeAnnuaire,
  ReglagesLivraison,
  ReservationPhotographe,
  SaisieAchatPhoto,
  StatutPreparation,
  TableauDeBordPhotographe,
  TarifPhotographe,
  VentePhotographe,
} from '@/types/photographe';

const donnees = <T>(r: { data: { data: T } }) => r.data.data;

export type ChampsPhoto = Partial<
  Pick<PhotoPhotographe, 'titre' | 'description' | 'prix' | 'publiee' | 'match_libelle' | 'formats_desactives'>
>;
export type ChampsFiche = Partial<
  Pick<FichePhotographe, 'nom_affiche' | 'bio' | 'ville' | 'telephone_reversement' | 'wallet_reversement' | 'couverture_photo_id'>
>;
export interface SaisieBoutique {
  formats?: Array<Omit<FormatCatalogue, 'id'> & { id?: string }>;
  livraison?: ReglagesLivraison;
}

/** Espace photographe, achat public et réservations des clubs. */
export const photographeService = {
  async inscrire(saisie: { prenom: string; nom: string; telephone: string; pin: string }) {
    return donnees<{ user: User; tokens: AuthTokens; photographe: FichePhotographe }>(
      await apiClient.post('/photographes/inscription', saisie)
    );
  },

  // --- Espace du photographe ---
  async moi() {
    return donnees<FichePhotographe>(await apiClient.get('/photographes/moi'));
  },
  async modifierMoi(champs: ChampsFiche) {
    return donnees<FichePhotographe>(await apiClient.patch('/photographes/moi', champs));
  },
  async remplacerTarifs(tarifs: Array<Omit<TarifPhotographe, 'id'> & { id?: string }>) {
    return donnees<FichePhotographe>(await apiClient.put('/photographes/moi/tarifs', { tarifs }));
  },
  async modifierBoutique(saisie: SaisieBoutique) {
    return donnees<FichePhotographe>(await apiClient.put('/photographes/moi/boutique', saisie));
  },
  async tableauDeBord() {
    return donnees<TableauDeBordPhotographe>(await apiClient.get('/photographes/moi/tableau-de-bord'));
  },
  async mesPhotos() {
    return donnees<PhotoPhotographe[]>(await apiClient.get('/photographes/moi/photos'));
  },
  /**
   * Une photo envoyée telle quelle dans le corps : pas de multipart, la
   * progression suit l'octet près et le serveur n'a rien à découper.
   */
  async envoyerPhoto(
    fichier: File,
    meta: { titre?: string; prix?: number; publiee?: boolean },
    progression?: (pourcent: number) => void
  ) {
    return donnees<PhotoPhotographe>(
      await apiClient.post('/photographes/moi/photos', fichier, {
        headers: { 'Content-Type': fichier.type },
        params: meta,
        onUploadProgress: (e) => {
          if (e.total) progression?.(Math.round((e.loaded / e.total) * 100));
        },
      })
    );
  },
  async modifierPhoto(id: string, champs: ChampsPhoto) {
    return donnees<PhotoPhotographe>(await apiClient.patch(`/photographes/moi/photos/${id}`, champs));
  },
  async supprimerPhoto(id: string) {
    await apiClient.delete(`/photographes/moi/photos/${id}`);
  },
  async mesVentes() {
    return donnees<VentePhotographe[]>(await apiClient.get('/photographes/moi/ventes'));
  },
  async avancerCommande(id: string, statut: Exclude<StatutPreparation, 'preparation'>) {
    return donnees<VentePhotographe>(await apiClient.post(`/photographes/moi/ventes/${id}/preparation`, { statut }));
  },
  async mesReservations() {
    return donnees<ReservationPhotographe[]>(await apiClient.get('/photographes/moi/reservations'));
  },
  async agirReservation(id: string, action: 'accepter' | 'refuser' | 'terminer', photoIds?: string[]) {
    return donnees<ReservationPhotographe>(
      await apiClient.post(`/photographes/moi/reservations/${id}/${action}`, photoIds ? { photo_ids: photoIds } : {})
    );
  },

  // --- Public ---
  async vitrine(limit = 12) {
    return donnees<CartePhotographe[]>(await apiClient.get('/photographes', { params: { limit } }));
  },
  async page(slug: string) {
    return donnees<PagePhotographe>(await apiClient.get(`/photographes/${encodeURIComponent(slug)}`));
  },
  async acheter(photoId: string, saisie: SaisieAchatPhoto) {
    return donnees<AchatPhoto>(await apiClient.post(`/photographes/photos/${photoId}/achat`, saisie));
  },
  async etatVente(id: string, jeton: string) {
    return donnees<EtatVente>(await apiClient.get(`/photographes/ventes/${id}`, { params: { jeton } }));
  },
  /** Adresse de téléchargement de l'original acheté : le jeton fait office de preuve. */
  cheminOriginalVente(id: string, jeton: string) {
    return `/api/photographes/ventes/${id}/original?jeton=${encodeURIComponent(jeton)}`;
  },

  // --- Club ---
  async annuaire() {
    return donnees<PhotographeAnnuaire[]>(await apiClient.get('/clubs/me/photographes'));
  },
  async reservationsClub() {
    return donnees<ReservationPhotographe[]>(await apiClient.get('/clubs/me/photographes/reservations'));
  },
  async reserver(saisie: { photographe_id: string; match_id: string; tarif_id: string; message?: string }) {
    return donnees<ReservationPhotographe>(await apiClient.post('/clubs/me/photographes/reservations', saisie));
  },
  async annulerReservation(id: string) {
    return donnees<ReservationPhotographe>(await apiClient.post(`/clubs/me/photographes/reservations/${id}/annuler`));
  },
  /**
   * Original livré au club : la route exige la session du club, qu'un simple
   * lien ne transmet pas. On le récupère donc en blob, puis on le propose au
   * téléchargement.
   */
  async telechargerOriginalClub(cheminApi: string, nomFichier: string) {
    const reponse = await apiClient.get(cheminApi.replace(/^\/api/, ''), { responseType: 'blob' });
    // Le nom choisi par le serveur porte la bonne extension (JPEG, PNG, WebP) ;
    // il n'est lisible que si l'en-tête est exposé, d'où le nom de repli.
    const disposition = String(reponse.headers['content-disposition'] ?? '');
    const nomServeur = /filename="([^"]+)"/.exec(disposition)?.[1];
    const url = URL.createObjectURL(reponse.data as Blob);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = nomServeur ?? nomFichier;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  },
};
